import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const NODE_STORAGE_SUMMARY_QUERY_KEY = 'node-storage-summary';

const getNodeStorageSummaryQueryKey = (params: {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
}) =>
  [
    NODE_STORAGE_SUMMARY_QUERY_KEY,
    params.organizationId,
    params.clusterId,
    params.nodeName,
  ] as const;

type NodeStorageSummaryQueryParams = {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
  enabled?: boolean;
};

const getNodeStorageSummaryQueryOptions = ({
  organizationId,
  clusterId,
  nodeName,
  enabled = true,
}: NodeStorageSummaryQueryParams) =>
  queryOptions({
    queryKey: getNodeStorageSummaryQueryKey({
      organizationId,
      clusterId,
      nodeName,
    }),
    queryFn: async () => {
      const { data } = await apiClient.stop.getNodeStorageSummary({
        organizationId: organizationId!,
        clusterId: clusterId!,
        nodeName: nodeName!,
      });

      return data;
    },
    enabled: makeIsEnabled(!!organizationId, !!clusterId, !!nodeName, enabled),
  });

type UseNodeStorageSummaryQueryArgs = {
  clusterId?: string;
  nodeName?: string;
  enabled?: boolean;
};

export const useNodeStorageSummaryQuery = ({
  clusterId,
  nodeName,
  enabled,
}: UseNodeStorageSummaryQueryArgs) => {
  const { currentOrganization } = useOrganizations();

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    getNodeStorageSummaryQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      nodeName,
      enabled,
    })
  );

  return {
    rootDisk: data?.storages?.find((s) => s.id === 'root')?.cloudVolumes?.[0],
    managedEphemeralDisks:
      data?.storages?.find((s) => s.id === 'managed-ephemeral')?.cloudVolumes ??
      [],
    isLoading: isLoading || isFetching,
    error,
    refetch,
  };
};
